// frontend/src/components/portfolio/CopyBlocksModal.tsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  Box,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';
import { Portfolio } from '../../types/Portfolio';
import portfolioService from '../../services/portfolioService';

interface CopyBlocksModalProps {
  open: boolean;
  onClose: () => void;
  selectedBlockIds: string[];
  currentPortfolioId: string;
  currentPortfolioTitle: string;
  onSuccess: (targetPortfolioTitle: string) => void;
}

const CopyBlocksModal: React.FC<CopyBlocksModalProps> = ({
  open,
  onClose,
  selectedBlockIds,
  currentPortfolioId,
  currentPortfolioTitle,
  onSuccess
}) => {
  const [copyMode, setCopyMode] = useState<'existing' | 'new'>('existing');
  const [targetPortfolioId, setTargetPortfolioId] = useState('');
  const [newPortfolioTitle, setNewPortfolioTitle] = useState('');
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load portfolios when modal opens
  useEffect(() => {
    if (open) {
      loadPortfolios();
    }
  }, [open]);

  const loadPortfolios = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await portfolioService.getAll();
      // Filter out current portfolio
      const filtered = data.filter(p => {
        const portfolioId = (p as any)._id || p.id;
        return portfolioId !== currentPortfolioId;
      });
      setPortfolios(filtered);
    } catch (err: any) {
      setError('Failed to load portfolios');
      console.error('Error loading portfolios:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError(null);

      if (copyMode === 'existing') {
        if (!targetPortfolioId) {
          setError('Please select a target portfolio');
          return;
        }

        // Copy blocks to existing portfolio
        await portfolioService.copyBlocksToPortfolio(
          currentPortfolioId,
          targetPortfolioId,
          selectedBlockIds
        );

        const targetPortfolio = portfolios.find(p => {
          const portfolioId = (p as any)._id || p.id;
          return portfolioId === targetPortfolioId;
        });

        onSuccess(targetPortfolio?.title || 'Selected Portfolio');
      } else {
        if (!newPortfolioTitle.trim()) {
          setError('Please enter a portfolio title');
          return;
        }

        // Create new portfolio with blocks
        const newPortfolio = await portfolioService.createPortfolioWithBlocks(
          currentPortfolioId,
          newPortfolioTitle.trim(),
          selectedBlockIds
        );

        onSuccess(newPortfolio.title);
      }

      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to copy blocks');
      console.error('Error copying blocks:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setCopyMode('existing');
    setTargetPortfolioId('');
    setNewPortfolioTitle('');
    setError(null);
    onClose();
  };

  const canSubmit = () => {
    if (submitting) return false;
    if (copyMode === 'existing') return !!targetPortfolioId;
    return !!newPortfolioTitle.trim();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Copy {selectedBlockIds.length} Block{selectedBlockIds.length !== 1 ? 's' : ''}
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary">
            Copying from: <strong>{currentPortfolioTitle}</strong>
          </Typography>

          <FormControl component="fieldset">
            <FormLabel component="legend">Destination</FormLabel>
            <RadioGroup
              value={copyMode}
              onChange={(e) => setCopyMode(e.target.value as 'existing' | 'new')}
            >
              <FormControlLabel
                value="existing"
                control={<Radio />}
                label="Copy to existing portfolio"
              />
              <FormControlLabel
                value="new"
                control={<Radio />}
                label="Create new portfolio"
              />
            </RadioGroup>
          </FormControl>

          {copyMode === 'existing' && (
            <FormControl fullWidth>
              <FormLabel sx={{ mb: 1 }}>Select Portfolio</FormLabel>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : portfolios.length === 0 ? (
                <Alert severity="info">
                  No other portfolios available. Create a new portfolio instead.
                </Alert>
              ) : (
                <Select
                  value={targetPortfolioId}
                  onChange={(e) => setTargetPortfolioId(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    Choose a portfolio...
                  </MenuItem>
                  {portfolios.map((portfolio) => {
                    const portfolioId = (portfolio as any)._id || portfolio.id;
                    return (
                      <MenuItem key={portfolioId} value={portfolioId}>
                        {portfolio.title}
                        {portfolio.investorContext?.targetInvestor && (
                          <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: 1 }}
                          >
                            ({portfolio.investorContext.targetInvestor})
                          </Typography>
                        )}
                      </MenuItem>
                    );
                  })}
                </Select>
              )}
            </FormControl>
          )}

          {copyMode === 'new' && (
            <TextField
              fullWidth
              label="New Portfolio Title"
              value={newPortfolioTitle}
              onChange={(e) => setNewPortfolioTitle(e.target.value)}
              placeholder="Enter portfolio name..."
              autoFocus
            />
          )}

          <Alert severity="info">
            Selected blocks will be copied (duplicated) and added to the end of the target portfolio.
          </Alert>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!canSubmit()}
          startIcon={submitting && <CircularProgress size={16} />}
        >
          {submitting ? 'Copying...' : 'Copy Blocks'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CopyBlocksModal;